import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { applyNodeChanges, useEdgesState, useNodesState } from '@xyflow/react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import LoadingView from '../../shared/components/LoadingView';
import TagTreeCanvas from '../components/tags/TagTreeCanvas';
import TagRelationPicker from '../components/tags/TagRelationPicker';
import WorkspaceView, { WorkspacePanel, PANEL_WIDTH } from '../components/layout/WorkspaceView';
import { layoutTags, computeOrphanIds } from '../utils/TagGraphLayout';
import useTags from '../hooks/useTags';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';

export default function TagsPage() {
  useDocumentTitle('Tags – Maintella');
  const { tags, loading, error, createTag, renameTag, deleteTag, mergeTag, addParent, removeParent } = useTags();
  const [selectedId, setSelectedId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const tagsById = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);
  const orphanIds = useMemo(() => computeOrphanIds(tags), [tags]);

  // Tags are the source of truth, so every change re-lays-out the full graph from scratch
  // (auto-organized, dagre-computed positions) rather than trying to preserve prior positions.
  useEffect(() => {
    const layout = layoutTags(tags);
    setNodes(layout.nodes);
    setEdges(layout.edges);
  }, [tags, setNodes, setEdges]);

  // Nodes (tags) are only deletable through the explicit Delete button + confirm dialog in the
  // sidebar, never via keypress — so 'remove' changes are filtered out here. Position/selection
  // changes still apply normally, same as edges below.
  const handleNodesChange = useCallback(
    (changes) => setNodes((current) => applyNodeChanges(changes.filter((c) => c.type !== 'remove'), current)),
    [setNodes],
  );

  // Delete key removes a selected edge, same as the workflow editor.
  const handleEdgesDelete = useCallback(
    async (deletedEdges) => {
      setActionError(null);
      await Promise.all(
        deletedEdges.map(async ({ source, target }) => {
          try {
            await removeParent(Number(target), Number(source));
          } catch (err) {
            setActionError(err.message);
          }
        }),
      );
    },
    [removeParent],
  );

  const flowNodes = useMemo(
    () => nodes.map((n) => ({ ...n, type: 'tag', selected: String(selectedId) === n.id })),
    [nodes, selectedId],
  );

  const selectedTag = selectedId != null ? tagsById[selectedId] : null;
  const orphans = tags.filter((t) => orphanIds.has(t.id));
  const childIds = selectedTag
    ? tags.filter((t) => (t.parent_ids || []).includes(selectedTag.id)).map((t) => t.id)
    : [];
  const systemTagIds = tags.filter((t) => t.is_system).map((t) => t.id);

  const clearAction = () => setActionError(null);

  const handleNewTag = async () => {
    const name = window.prompt('New tag name');
    if (!name) return;
    clearAction();
    try {
      const tag = await createTag(name);
      setSelectedId(tag.id);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleRename = async () => {
    const name = window.prompt('Rename tag', selectedTag.name);
    if (!name || name === selectedTag.name) return;
    clearAction();
    try {
      await renameTag(selectedTag.id, name);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${selectedTag.name}"?`)) return;
    clearAction();
    try {
      await deleteTag(selectedTag.id);
      setSelectedId(null);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleMerge = async () => {
    if (!mergeTargetId) return;
    clearAction();
    try {
      await mergeTag(selectedTag.id, Number(mergeTargetId));
      setSelectedId(Number(mergeTargetId));
      setMergeTargetId('');
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleAddParent = async (parentId) => {
    clearAction();
    try {
      await addParent(selectedTag.id, parentId);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleRemoveParent = async (parentId) => {
    clearAction();
    try {
      await removeParent(selectedTag.id, parentId);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleAddChild = async (childId) => {
    clearAction();
    try {
      await addParent(childId, selectedTag.id);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleRemoveChild = async (childId) => {
    clearAction();
    try {
      await removeParent(childId, selectedTag.id);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleConnect = async ({ source, target }) => {
    clearAction();
    try {
      // Canvas edge is drawn source (parent) -> target (child).
      await addParent(Number(target), Number(source));
    } catch (err) {
      setActionError(err.message);
    }
  };

  return (
    <>
      <ObjectTopBar
        title="Tags"
        primaryBtn={{ btnText: 'New tag', btnIcon: 'bi-plus-lg', variant: 'primary', onClick: handleNewTag }}
      />
      {loading && tags.length === 0 ? (
        <div className="container-fluid py-4">
          <LoadingView className="py-5" symbolClass="bi-tags" title="Loading tags…" />
        </div>
      ) : (
        <WorkspaceView>
          <div className="flex-grow-1" style={{ minWidth: 0, minHeight: 0, height: '100%' }}>
            <TagTreeCanvas
              nodes={flowNodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={onEdgesChange}
              onEdgesDelete={handleEdgesDelete}
              onConnect={handleConnect}
              onNodeClick={(_, node) => setSelectedId(Number(node.id))}
              onPaneClick={() => setSelectedId(null)}
            />
          </div>

          <WorkspacePanel
            width={PANEL_WIDTH}
            side="right"
            header={!selectedTag ? (
                <>
                  <div className="small text-muted mb-1">Tags</div>
                  <h5 className="fw-semibold mb-0">Orphan tags</h5>
                </>
              ) : (
                <div className="d-flex justify-content-between align-items-start">
                  <div className="min-w-0">
                    <div className="small text-muted mb-1">Tag</div>
                    <h5 className="fw-semibold mb-0 text-truncate">
                      {selectedTag.is_system && <i className="bi bi-lock-fill me-1" />}
                      {selectedTag.name}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close flex-shrink-0 ms-2"
                    aria-label="Close"
                    onClick={() => setSelectedId(null)}
                  />
                </div>
              )}
          >
              {error && (
                <div className="alert alert-danger small" role="alert">
                  <i className="bi bi-exclamation-triangle me-1" />
                  {error}
                </div>
              )}
              {actionError && (
                <div className="alert alert-danger small" role="alert">
                  <i className="bi bi-exclamation-triangle me-1" />
                  {actionError}
                </div>
              )}

              {!selectedTag ? (
                <>
                  <p className="text-muted small mb-3">Not yet placed under a parent tag.</p>
                  {orphans.length === 0 ? (
                    <p className="text-muted small">None</p>
                  ) : (
                    <ul className="list-group">
                      {orphans
                        .slice()
                        .sort((a, b) => (b.asset_count || 0) - (a.asset_count || 0))
                        .map((tag) => (
                          <li key={tag.id} className="list-group-item p-0 border-0">
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-start w-100"
                              onClick={() => setSelectedId(tag.id)}
                            >
                              {tag.name} <span className="text-muted">({tag.asset_count || 0})</span>
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </>
              ) : (
                <>
                  <p className="text-muted small">{selectedTag.asset_count || 0} matching assets</p>

                  {!selectedTag.is_system && (
                    <div className="d-flex gap-2 mb-4">
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleRename}>
                        Rename
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleDelete}>
                        Delete
                      </button>
                    </div>
                  )}

                  {!selectedTag.is_system && (
                    <section className="mb-4" aria-label="Parents">
                      <h6 className="text-muted fw-normal mb-3">Parents</h6>
                      {/* Global is a valid parent (clears the child from the orphan list) — it
                          just can't receive a parent itself, which is why it's excluded from
                          the Children picker below and from the Merge list further down. */}
                      <TagRelationPicker
                        tags={tags}
                        selectedIds={selectedTag.parent_ids || []}
                        excludeIds={[selectedTag.id]}
                        onAdd={handleAddParent}
                        onRemove={handleRemoveParent}
                        placeholder="Add parent…"
                        emptyHint="None (root tag)"
                      />
                    </section>
                  )}

                  {!selectedTag.is_system && (
                    <section className="mb-4" aria-label="Children">
                      <h6 className="text-muted fw-normal mb-3">Children</h6>
                      <TagRelationPicker
                        tags={tags}
                        selectedIds={childIds}
                        excludeIds={[selectedTag.id, ...systemTagIds]}
                        onAdd={handleAddChild}
                        onRemove={handleRemoveChild}
                        placeholder="Add child…"
                        emptyHint="None"
                      />
                    </section>
                  )}

                  {!selectedTag.is_system && (
                    <section className="mb-4">
                      <h6 className="text-muted fw-normal mb-3">Merge into…</h6>
                      <div className="d-flex gap-1">
                        <select
                          className="form-select form-select-sm"
                          aria-label="Merge target tag"
                          value={mergeTargetId}
                          onChange={(e) => setMergeTargetId(e.target.value)}
                        >
                          <option value="">Select target tag…</option>
                          {tags
                            .filter((t) => t.id !== selectedTag.id && !t.is_system)
                            .map((t) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                        <button type="button" className="btn btn-sm btn-outline-warning" onClick={handleMerge}>
                          Merge
                        </button>
                      </div>
                    </section>
                  )}
                </>
              )}
          </WorkspacePanel>
        </WorkspaceView>
      )}
    </>
  );
}
